// [NEW FILE] PM-12: "Sign in with Microsoft" (Microsoft Entra ID) through MSAL.
//
// Flow (matches the backend: app/api/auth.py POST /auth/entra/exchange, app/services/entra.py):
//   1. MSAL opens the Microsoft sign-in pop-up and gets an ACCESS TOKEN for the PMS API scope
//      (REACT_APP_ENTRAID_API_SCOPE, e.g. api://<backend API client id>/access_as_user).
//   2. The token is sent to POST /auth/entra/exchange { access_token }.
//   3. The backend checks signature, tenant, audience and scope, finds the PMS employee (linked identity, or the
//      active account whose official e-mail equals the Microsoft sign-in name) and returns the normal PMS session.
//
// .env (frontend):
//   REACT_APP_ENTRA_ENABLED=true
//   REACT_APP_ENTRAID_CLIENT_ID=<SPA app registration client id>
//   REACT_APP_ENTRAID_TENANT_ID=<tenant id>
//   REACT_APP_ENTRAID_API_SCOPE=api://<backend API client id>/access_as_user
// Azure: add <site>/auth-redirect.html as a "Single-page application" redirect URI of the SPA registration.
import { PublicClientApplication, InteractionRequiredAuthError, BrowserAuthError } from "@azure/msal-browser";

const env = (name) => String(process.env[name] || "").trim();

export const ENTRA = {
  enabled: env("REACT_APP_ENTRA_ENABLED").toLowerCase() === "true",
  clientId: env("REACT_APP_ENTRAID_CLIENT_ID"),
  tenantId: env("REACT_APP_ENTRAID_TENANT_ID"),
  scope: env("REACT_APP_ENTRAID_API_SCOPE"),
  label: env("REACT_APP_ENTRA_BUTTON_LABEL") || "Sign in with Microsoft",
};

export const isEntraConfigured = () => Boolean(ENTRA.enabled && ENTRA.clientId && ENTRA.tenantId && ENTRA.scope);

let pcaPromise = null;
function client() {
  if (!pcaPromise) {
    const pca = new PublicClientApplication({
      auth: {
        clientId: ENTRA.clientId,
        authority: `https://login.microsoftonline.com/${ENTRA.tenantId}`,
        // A blank page for the pop-up to land on, so the PMS app does not start inside the pop-up.
        redirectUri: `${window.location.origin}/auth-redirect.html`,
      },
      cache: { cacheLocation: "sessionStorage" },
    });
    pcaPromise = pca.initialize().then(() => pca);
  }
  return pcaPromise;
}

// Returns a Microsoft access token for the PMS API (opens the pop-up when needed).
export async function getMicrosoftAccessToken() {
  if (!isEntraConfigured()) throw new Error("Microsoft sign-in is not configured in this app.");
  const pca = await client();
  const request = { scopes: [ENTRA.scope], prompt: "select_account" };
  const account = pca.getAllAccounts()[0];
  if (account) {
    try {
      const silent = await pca.acquireTokenSilent({ scopes: [ENTRA.scope], account });
      if (silent?.accessToken) return silent.accessToken;
    } catch (err) {
      if (!(err instanceof InteractionRequiredAuthError)) { /* fall through to the pop-up */ }
    }
  }
  try {
    const result = await pca.loginPopup(request);
    if (result?.accessToken) return result.accessToken;
    const token = await pca.acquireTokenSilent({ scopes: [ENTRA.scope], account: result.account });
    return token.accessToken;
  } catch (err) {
    if (err instanceof BrowserAuthError && /popup_window_error|empty_window_error/.test(err.errorCode || "")) {
      throw new Error("The Microsoft sign-in window was blocked. Allow pop-ups for this site and try again.");
    }
    if (err instanceof BrowserAuthError && /user_cancelled/.test(err.errorCode || "")) {
      throw new Error("Microsoft sign-in was cancelled.");
    }
    throw err;
  }
}

// Clears the Microsoft account cached in this browser tab (on sign-out; the PMS session is revoked separately).
export async function clearMicrosoftAccount() {
  if (!isEntraConfigured() || !pcaPromise) return;
  try { await (await client()).clearCache(); } catch { /* nothing to clear */ }
}
